import React, { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import GoogleMapReact from 'google-map-react';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import { platformSettingsService } from '../services/central-api/platform-settings';
import moment from 'moment';
import { translate } from '../services/translate';

export default function DrawRouteMap({ cameraPositions, newZoom = 11, controlSize, zoomControl = false, useFullscreen = false }: {
    cameraPositions: {
        latitude: number;
        longitude: number;
        timestamp: number;
        selected?: boolean;
    }[];
    newZoom?: number; controlSize?: number; zoomControl?: boolean; useFullscreen?: boolean;
}) {
    const { styles } = useStyles(styleSheet);

    const [map, setMap] = useState<google.maps.Map>();
    const [maps] = useState<typeof google.maps>(window?.google?.maps);
    const [zoom, setZoom] = useState(11);
    const [defaultZoom, setDefaultZoom] = useState<number>(11);
    const [defaultCenter, setDefaultCenter] = useState<GoogleMapReact.Coords | undefined>();
    const { data } = platformSettingsService.useMapCentralPoint();

    useEffect(() => {
        if (newZoom && newZoom !== zoom) {
            setZoom(newZoom);
        }
    }, [newZoom]);

    useEffect(() => {
        if (data) {
            setDefaultCenter({ lat: data.latitude, lng: data.longitude });
            setDefaultZoom(data.zoom);
        }
    }, [data]);

    const overlaysRef = useRef<(
        | google.maps.Marker
        | google.maps.Polyline
        | google.maps.Polygon
        | google.maps.Circle
        | google.maps.Rectangle
    )[]>([]);

    const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

    useEffect(() => {
        if (!map || !maps) {
            return;
        }

        try {
            overlaysRef.current.forEach(overlay => {
                overlay.setMap(null);
            });

            overlaysRef.current = [];

            infoWindowRef.current?.close();

            if (cameraPositions.length) {
                const path = cameraPositions.map(point => ({
                    lat: point.latitude,
                    lng: point.longitude,
                }));

                const polyline = new maps.Polyline({
                    path,
                    geodesic: true,
                    strokeColor: '#1976D2',
                    strokeOpacity: 1,
                    strokeWeight: 5,
                    map,
                });

                overlaysRef.current.push(polyline);

                if (!infoWindowRef.current) {
                    infoWindowRef.current = new maps.InfoWindow();
                }

                const infoWindow = infoWindowRef.current;

                const bounds = new maps.LatLngBounds();

                cameraPositions.forEach((point, index) => {
                    const position = {
                        lat: point.latitude,
                        lng: point.longitude,
                    };

                    bounds.extend(position);

                    const isFirst = index === 0;
                    const isLast = index === cameraPositions.length - 1;

                    const marker = new maps.Marker({
                        position,
                        map,
                        title: String(point.timestamp),
                        icon: {
                            path: maps.SymbolPath.CIRCLE,
                            scale: point.selected
                                ? 10
                                : isFirst || isLast
                                    ? 7
                                    : 4,
                            fillColor: point.selected
                                ? '#F9D76B'
                                : isFirst
                                    ? '#22C55E'
                                    : isLast
                                        ? '#EF4444'
                                        : '#1976D2',
                            fillOpacity: 1,
                            strokeColor: '#FFFFFF',
                            strokeWeight: 1,
                        },
                    });

                    overlaysRef.current.push(marker);

                    marker.addListener('click', () => {
                        infoWindow.setContent(`<div>
                            <strong>${translate('time')}</strong>
                            <br />
                            ${moment(point.timestamp).format('DD/MM/YYYY - HH:mm:ss')}
                            <br />
                            <strong>${translate('localization')}</strong>
                            <br />
                            Lat: ${point.latitude}
                            <br />
                            Lng: ${point.longitude}
                        </div>
                    `);

                        infoWindow.open({
                            map,
                            anchor: marker,
                        });
                    });
                });

                map.fitBounds(bounds);
            } else if (defaultCenter) {
                map.setCenter(defaultCenter);
                map.setZoom(defaultZoom);
            }
        } catch (e) {
            console.error(e);
        }

        return () => {
            overlaysRef.current.forEach(overlay => {
                overlay.setMap(null);
            });

            overlaysRef.current = [];

            infoWindowRef.current?.close();
        };
    }, [
        map,
        maps,
        cameraPositions,
        defaultCenter,
        defaultZoom,
        translate,
    ]);

    return (
        <View style={styles.mapContainer}>
            {defaultCenter &&
                <GoogleMapReact
                    center={defaultCenter}
                    draggable={true}
                    defaultZoom={defaultZoom}
                    zoom={defaultZoom}
                    options={() => ({
                        streetViewControl: false,
                        fullscreenControl: useFullscreen,
                        controlSize: controlSize ?? undefined,
                        zoomControl: zoomControl,
                        styles: UnistylesRuntime.themeName === 'light' || UnistylesRuntime.themeName == 'lightAlt' ? [{
                            featureType: 'poi',
                            elementType: 'labels',
                            stylers: [{ visibility: 'off' }],
                        },
                        {
                            featureType: 'transit',
                            stylers: [{ visibility: 'off' }],
                        }] : [
                            { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                            { featureType: 'transit', stylers: [{ visibility: 'off' }] },
                            { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
                            { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
                            { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
                            {
                                featureType: 'administrative.locality',
                                elementType: 'labels.text.fill',
                                stylers: [{ color: '#d59563' }],
                            },
                            {
                                featureType: 'road',
                                elementType: 'geometry',
                                stylers: [{ color: '#38414e' }],
                            },
                            {
                                featureType: 'road',
                                elementType: 'geometry.stroke',
                                stylers: [{ color: '#212a37' }],
                            },
                            {
                                featureType: 'road',
                                elementType: 'labels.text.fill',
                                stylers: [{ color: '#9ca5b3' }],
                            },
                            {
                                featureType: 'road.highway',
                                elementType: 'geometry',
                                stylers: [{ color: '#746855' }],
                            },
                            {
                                featureType: 'road.highway',
                                elementType: 'geometry.stroke',
                                stylers: [{ color: '#1f2835' }],
                            },
                            {
                                featureType: 'road.highway',
                                elementType: 'labels.text.fill',
                                stylers: [{ color: '#f3d19c' }],
                            },
                            {
                                featureType: 'water',
                                elementType: 'geometry',
                                stylers: [{ color: '#17263c' }],
                            },
                            {
                                featureType: 'water',
                                elementType: 'labels.text.fill',
                                stylers: [{ color: '#515c6d' }],
                            },
                            {
                                featureType: 'water',
                                elementType: 'labels.text.stroke',
                                stylers: [{ color: '#17263c' }],
                            },
                        ]
                    })}
                    yesIWantToUseGoogleMapApiInternals
                    onGoogleApiLoaded={({ map }: { map: google.maps.Map; }) => {
                        setMap(map);
                    }}>
                </GoogleMapReact>
            }
        </View >
    );
}


const styleSheet = createStyleSheet(() => ({
    mapContainer: {
        flex: 1
    }
}));

